'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface CoatingStep {
  id: number;
  title: string;
  description: string;
  image: string;
  alt: string;
  details: string[];
}

const coatingSteps: CoatingStep[] = [
  {
    id: 1,
    title: 'Pre-treatment',
    description: 'Advanced zirconium-based chemistry for superior adhesion and corrosion resistance. Multi-stage cleaning and surface preparation.',
    image: "https://xdfjtjgomqbjrrhygnbj.supabase.co/storage/v1/object/public/powder-coating-processes/pre-treatment.jpg",
    alt: 'Metal parts undergoing pre-treatment process with zirconium-based chemistry in industrial cleaning system',
    details: [
      'Alkaline Cleaning',
      'Zirconium Conversion Coating',
      'Multi-Stage Rinsing',
      'Surface Quality Verification'
    ]
  },
  {
    id: 2,
    title: 'Spray Application',
    description: 'Electrostatic powder application with precise film thickness control. Automated conveyor system ensures consistent coverage.',
    image: "https://xdfjtjgomqbjrrhygnbj.supabase.co/storage/v1/object/public/powder-coating-processes/spray-application.jpg",
    alt: 'Automated electrostatic powder coating spray booth applying powder to metal parts on conveyor system',
    details: [
      'Electrostatic Application',
      'Film Thickness Control',
      'Coverage Uniformity',
      'Powder Recovery System'
    ]
  },
  {
    id: 3,
    title: 'Cure & Finish',
    description: 'Precision temperature-controlled curing process. Infrared monitoring ensures complete cross-linking and optimal finish quality.',
    image: "https://xdfjtjgomqbjrrhygnbj.supabase.co/storage/v1/object/public/powder-coating-processes/cure-finish.jpg",
    alt: 'Industrial curing oven with temperature control system processing powder-coated parts at optimal cure temperature',
    details: [
      'Temperature Profiling',
      'Cure Time Monitoring',
      'Cross-Link Verification',
      'Cooling Protocol'
    ]
  },
  {
    id: 4,
    title: 'Quality Inspection',
    description: 'Comprehensive quality verification including film thickness, adhesion testing, and visual inspection to ensure coating performance.',
    image: "https://xdfjtjgomqbjrrhygnbj.supabase.co/storage/v1/object/public/powder-coating-processes/quality-inspection.jpg",
    alt: 'Quality inspector measuring coating thickness and performing adhesion tests on finished powder-coated parts',
    details: [
      'Film Thickness Measurement',
      'Adhesion Testing',
      'Visual Inspection',
      'Performance Documentation'
    ]
  }
];

export default function PowderCoatingProcessShowcase() {
  const [activeStep, setActiveStep] = useState(1);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleStepClick = (id: number) => {
    if (!isHydrated) return;
    setActiveStep(id);
  };

  const currentStep = coatingSteps.find((step) => step.id === activeStep) || coatingSteps[0];

  return (
    <section className="lg:py-24 bg-background py-[70px]">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="SparklesIcon" size={20} variant="solid" className="text-primary" />
            <span className="text-sm font-cta font-semibold text-primary">Process Excellence</span>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-6">
            Powder Coating Process
          </h2>
          
          <p className="text-lg text-muted-foreground leading-relaxed">
            Our systematic powder coating process delivers superior finish quality with advanced chemistry, precise application control, and comprehensive quality verification.
          </p>
        </div>
        
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {coatingSteps.map((step) => (
              <button
                key={step.id}
                onClick={() => handleStepClick(step.id)}
                className={`p-4 rounded-lg border-2 transition-all duration-base text-left ${
                  activeStep === step.id
                    ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/50'
                }`}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-headline font-bold text-sm ${
                      activeStep === step.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {step.id}
                  </div>
                  <h3
                    className={`font-headline font-bold text-sm lg:text-base ${
                      activeStep === step.id ? 'text-primary' : 'text-foreground'
                    }`}
                  >
                    {step.title}
                  </h3>
                </div>
              </button>
            ))}
          </div>
          
          <div className="bg-card rounded-xl shadow-lg overflow-hidden border border-border">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative h-64 lg:h-auto">
                <AppImage
                  key={currentStep.id}
                  src={currentStep.image}
                  alt={currentStep.alt}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="p-8 lg:p-12">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                    <span className="text-2xl font-headline font-bold text-primary-foreground">
                      {currentStep.id}
                    </span>
                  </div>
                  <h3 className="text-2xl font-headline font-bold text-foreground">
                    {currentStep.title}
                  </h3>
                </div>
                
                <p className="text-base text-muted-foreground mb-6 leading-relaxed">
                  {currentStep.description}
                </p>
                
                <div className="space-y-3">
                  <h4 className="text-sm font-cta font-semibold text-foreground uppercase tracking-wide">
                    Key Activities
                  </h4>
                  <ul className="space-y-2">
                    {currentStep.details.map((detail, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <Icon
                          name="CheckCircleIcon"
                          size={20}
                          variant="solid"
                          className="text-primary flex-shrink-0 mt-0.5"
                        />
                        <span className="text-sm text-muted-foreground">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}